import { Button } from '@cluey/cluey-components';
import { useCallback, useId, useState } from 'react';

import RadioGroup, { RadioGroupOption } from '../../../../common/RadioGroup/RadioGroup';
import { Dropdown } from '../../../../legacy/components';
import { PauseEnrolmentContextType, usePauseEnrolment } from '../../PauseEnrolmentContext';
import useSchoolPeriods from '../../hooks/useSchoolPeriods';
import ClosureDatesNotice from './ClosureDatesNotice';
import { createPauseMethods } from './createPauseMethods';
import { createPauseResumptionOptions } from './createPauseResumptionOptions';
import { validateForm } from './validation';

export type PauseEnrolmentFormProps = {
  submit: (values: { pauseStartDate: string; resumptionDate: string }) => void;
  submitError?: string;
  // #TODO refactor creation of options
  overrideCreatePauseMethods?: (context: PauseEnrolmentContextType) => RadioGroupOption[];
  overrideCreatePauseResumptionOptions?: (context: PauseEnrolmentContextType) => RadioGroupOption[];
};

const PauseEnrolmentForm = ({
  submit,
  submitError,
  overrideCreatePauseMethods,
  overrideCreatePauseResumptionOptions,
}: PauseEnrolmentFormProps) => {
  const {
    pauseMethods,
    pauseMethod,
    setPauseMethod,
    pauseByWeeksValue,
    pauseResumptionOptions,
    pauseResumptionMethod,
    setPauseResumptionMethod,
    pauseStartDate,
    resumptionDate,
    setValidationErrors,
    validationErrors,
    nextSchoolTerm,
  } = usePauseEnrolment({
    createPauseMethods: overrideCreatePauseMethods || createPauseMethods,
    createPauseResumptionOptions: overrideCreatePauseResumptionOptions || createPauseResumptionOptions,
  });
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);
  const submitHandler = useCallback(() => {
    if (!hasSubmittedOnce) {
      setHasSubmittedOnce(true);
    }
    const validationResult = validateForm(
      {
        pauseStartDate,
        resumptionDate,
        pauseByWeeksValue,
        pauseMethod,
        pauseResumptionMethod,
      },
      nextSchoolTerm
    );

    if (!validationResult.success && !!validationResult.error) {
      setValidationErrors(validationResult.error);
    } else {
      submit({ pauseStartDate, resumptionDate });
    }
  }, [
    hasSubmittedOnce,
    pauseStartDate,
    resumptionDate,
    setValidationErrors,
    submit,
    pauseByWeeksValue,
    pauseMethod,
    pauseResumptionMethod,
    nextSchoolTerm,
  ]);
  const pauseStartDateErrors = validationErrors?.pauseStartDate?._errors || null;
  const resumptionDateErrors =
    validationErrors?.resumptionDate?._errors ||
    validationErrors?.resumptionDate?.['resume-on-specific-date']?._errors ||
    null;
  const showErrors = !pauseMethod
    ? pauseStartDateErrors
    : !pauseStartDateErrors && !pauseResumptionMethod && resumptionDateErrors;

  const { currentOrUpcomingHoliday } = useSchoolPeriods();
  const isSummerHoliday = currentOrUpcomingHoliday?.type === 'Summer Holiday';
  const specificDateElementId = useId();

  return (
    <div>
      <section>
        <div className="mb-5 font-display text-xl font-bold">When would you like to pause sessions?</div>
        <RadioGroup name="pause-method" options={pauseMethods} value={pauseMethod} onChange={setPauseMethod} />
        {isSummerHoliday && (
          <div className="mt-6">
            <ClosureDatesNotice />
          </div>
        )}
      </section>
      {pauseMethod === 'pause-by-specific-date' && (
        <section className="mt-5">
          <div className="mb-5 font-display text-xl font-bold">When do you want to resume sessions?</div>
          <div className="mb-4 w-fit">
            <Dropdown
              id="resumption-method"
              options={pauseResumptionOptions.map((option) => ({
                ...option,
                text: option.labelForDropdown || option.value,
              }))}
              selectedOption={pauseResumptionMethod}
              setSelectedOption={setPauseResumptionMethod}
            />

            {pauseResumptionMethod === 'resume-on-specific-date' &&
              pauseResumptionOptions
                .find((option) => option.value === 'resume-on-specific-date')
                ?.renderBelowDropdownOption({
                  id: specificDateElementId,
                  checked: pauseResumptionMethod === 'resume-on-specific-date',
                  error: false,
                  disabled: false,
                  groupName: 'pause-resumption',
                  value: 'resume-on-specific-date',
                })}
          </div>
        </section>
      )}
      {showErrors && hasSubmittedOnce && <div className="mt-4 text-red-5">Please select a date.</div>}
      {submitError && <div className="mt-4 text-red-5">{submitError}</div>}
      <Button className="mt-10" size="lg" onClick={submitHandler}>
        Confirm pause
      </Button>
    </div>
  );
};

export default PauseEnrolmentForm;
